import { buildWsUrl, env } from '@/lib/env';
import { useAuthStore } from '@/stores/auth';
import type { AdminChannel, ChannelPayloadMap, WsClientFrame, WsServerEnvelope } from './types';

type ChannelHandler<C extends AdminChannel> = (payload: ChannelPayloadMap[C]) => void;

interface SubscribeMeta {
  channel: AdminChannel;
  payload?: unknown;
}

const PING_INTERVAL_MS = 25_000;
const PONG_TIMEOUT_MS = 5_000;
const MAX_BACKOFF_MS = 60_000;

/**
 * 单例 WS 客户端 — 一个浏览器 tab 一个连接,通过 op:subscribe 复用 channel。
 *
 * 行为:
 * - 启动时如已有 token 会自动 connect();登出 / 401 触发 disconnect()
 * - 服务端每 25s 发 ping,客户端 5s 内回 pong;客户端也每 25s 主动发一次 pong 健康
 * - 断线指数退避 1s → 2s → 4s → ... → 60s,重连后自动重发所有订阅
 * - 多个组件订阅同一 channel 共享底层订阅,最后一个取消时才发 unsubscribe
 */
class WsClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongDeadline: ReturnType<typeof setTimeout> | null = null;
  private backoffMs = 1_000;
  private reconnectAttempts = 0;

  private wantConnected = false;
  private subscriptions = new Map<AdminChannel, Set<(payload: unknown) => void>>();
  private channelMeta = new Map<AdminChannel, SubscribeMeta>();
  private statusListeners = new Set<(state: WsConnectionState) => void>();

  /**
   * quote 频道单独走「按 symbolId 多路订阅」。
   * 其余频道是「一个 channel 一个订阅」,但 quote 不同:后端 quote 订阅是 per-symbol 的
   * (subscribe payload 必带 symbolId,后端据此向 cTrader 订阅该品种 spot),
   * 需要为每个 symbolId 各发一条 subscribe,并按引用计数在最后一个取消时才 unsubscribe。
   */
  private quoteSymbols = new Map<string, number>(); // symbolId → 引用计数
  private quoteHandlers = new Set<(payload: ChannelPayloadMap['quote']) => void>();

  private state: WsConnectionState = 'idle';

  connect(): void {
    if (this.wantConnected) return;
    const token = useAuthStore.getState().token;
    if (!token) {
      if (env.isDev) console.warn('[ws] connect() called without token, ignored');
      return;
    }
    this.wantConnected = true;
    this.openSocket(token);
  }

  disconnect(): void {
    this.wantConnected = false;
    this.clearTimers();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close(1000, 'client disconnect');
      this.ws = null;
    }
    this.setState('idle');
  }

  /** 订阅一个频道。返回取消订阅函数。 */
  subscribe<C extends AdminChannel>(channel: C, handler: ChannelHandler<C>, payload?: unknown): () => void {
    let handlers = this.subscriptions.get(channel);
    if (!handlers) {
      handlers = new Set();
      this.subscriptions.set(channel, handlers);
      this.channelMeta.set(channel, { channel, payload });
      this.send({ op: 'subscribe', channel, payload });
    }
    handlers.add(handler as (payload: unknown) => void);
    return () => this.unsubscribe(channel, handler);
  }

  private unsubscribe<C extends AdminChannel>(channel: C, handler: ChannelHandler<C>): void {
    const handlers = this.subscriptions.get(channel);
    if (!handlers) return;
    handlers.delete(handler as (payload: unknown) => void);
    if (handlers.size === 0) {
      this.subscriptions.delete(channel);
      const meta = this.channelMeta.get(channel);
      this.channelMeta.delete(channel);
      this.send({ op: 'unsubscribe', channel, payload: meta?.payload });
    }
  }

  /**
   * 订阅一组品种的实时行情(quote 频道)。返回取消订阅函数。
   *
   * - 多个组件可订阅不同 symbolId 集合,handler 会收到「所有」已订阅品种的 quote,
   *   由调用方按 payload.symbolId 自行分发(通常写进 quote store)。
   * - 同一 symbolId 被多处订阅时引用计数累加,最后一个取消时才向后端 unsubscribe。
   */
  subscribeQuote(symbolIds: string[], handler: (payload: ChannelPayloadMap['quote']) => void): () => void {
    this.quoteHandlers.add(handler);
    for (const id of symbolIds) {
      const n = this.quoteSymbols.get(id) ?? 0;
      this.quoteSymbols.set(id, n + 1);
      if (n === 0) this.send({ op: 'subscribe', channel: 'quote', payload: { symbolId: id } });
    }
    return () => {
      this.quoteHandlers.delete(handler);
      for (const id of symbolIds) {
        const n = (this.quoteSymbols.get(id) ?? 1) - 1;
        if (n <= 0) {
          this.quoteSymbols.delete(id);
          this.send({ op: 'unsubscribe', channel: 'quote', payload: { symbolId: id } });
        } else {
          this.quoteSymbols.set(id, n);
        }
      }
    };
  }

  onStatusChange(listener: (state: WsConnectionState) => void): () => void {
    this.statusListeners.add(listener);
    listener(this.state);
    return () => this.statusListeners.delete(listener);
  }

  // ─── private ──────────────────────────────────────────────────

  private openSocket(token: string): void {
    this.setState(this.reconnectAttempts === 0 ? 'connecting' : 'reconnecting');
    const url = buildWsUrl(token);
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      if (env.isDev) console.error('[ws] failed to construct WebSocket', err);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.backoffMs = 1_000;
      this.reconnectAttempts = 0;
      this.setState('open');
      this.startHeartbeat();
      // 重连后,把所有当前订阅再发一次
      for (const meta of this.channelMeta.values()) {
        this.send({ op: 'subscribe', channel: meta.channel, payload: meta.payload });
      }
      // quote 按 symbolId 逐个重新订阅(后端连接会话级订阅,重连后会丢)
      for (const id of this.quoteSymbols.keys()) {
        this.send({ op: 'subscribe', channel: 'quote', payload: { symbolId: id } });
      }
    };

    ws.onmessage = (event) => this.handleMessage(event.data);

    ws.onerror = (err) => {
      if (env.isDev) console.warn('[ws] error', err);
    };

    ws.onclose = () => {
      this.clearTimers();
      this.ws = null;
      if (this.wantConnected) {
        this.scheduleReconnect();
      } else {
        this.setState('idle');
      }
    };
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let frame: WsServerEnvelope;
    try {
      frame = JSON.parse(raw) as WsServerEnvelope;
    } catch {
      if (env.isDev) console.warn('[ws] non-JSON frame discarded', raw);
      return;
    }
    switch (frame.op) {
      case 'ping': {
        this.send({ op: 'pong' });
        return;
      }
      case 'pong': {
        if (this.pongDeadline) {
          clearTimeout(this.pongDeadline);
          this.pongDeadline = null;
        }
        return;
      }
      case 'subscribed':
        return;
      case 'error': {
        if (env.isDev) console.warn('[ws] server error', frame.code, frame.message);
        return;
      }
      case 'event': {
        if (!frame.channel) return;
        // quote 走专用多路分发(所有 quote handler 收到全部品种,自行按 symbolId 过滤)
        if (frame.channel === 'quote') {
          const payload = frame.payload as ChannelPayloadMap['quote'];
          for (const h of this.quoteHandlers) {
            try {
              h(payload);
            } catch (err) {
              if (env.isDev) console.error('[ws] quote handler threw', err);
            }
          }
          return;
        }
        const handlers = this.subscriptions.get(frame.channel);
        if (!handlers || handlers.size === 0) return;
        for (const h of handlers) {
          try {
            h(frame.payload);
          } catch (err) {
            if (env.isDev) console.error('[ws] handler threw', err);
          }
        }
        return;
      }
      default:
        return;
    }
  }

  private send(frame: WsClientFrame): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(frame));
    } catch (err) {
      if (env.isDev) console.warn('[ws] send failed', err);
    }
  }

  private startHeartbeat(): void {
    this.clearTimers();
    this.pingTimer = setInterval(() => {
      this.send({ op: 'ping' });
      // 5s 内没收到 pong 就强行断开,触发重连
      if (this.pongDeadline) clearTimeout(this.pongDeadline);
      this.pongDeadline = setTimeout(() => {
        if (env.isDev) console.warn('[ws] pong timeout, forcing reconnect');
        this.ws?.close(4000, 'pong timeout');
      }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);
  }

  private scheduleReconnect(): void {
    if (!this.wantConnected) return;
    this.setState('reconnecting');
    this.reconnectAttempts += 1;
    const delay = Math.min(this.backoffMs, MAX_BACKOFF_MS);
    this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      const token = useAuthStore.getState().token;
      if (!token) {
        this.wantConnected = false;
        this.setState('idle');
        return;
      }
      this.openSocket(token);
    }, delay);
  }

  private clearTimers(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.pongDeadline) {
      clearTimeout(this.pongDeadline);
      this.pongDeadline = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setState(s: WsConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    for (const l of this.statusListeners) l(s);
  }
}

export type WsConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting';

export const wsClient = new WsClient();

/** 根据 auth store 自动 connect / disconnect。在 main.tsx 调用一次即可。 */
export function bindWsToAuth(): () => void {
  return useAuthStore.subscribe((state, prev) => {
    if (state.token && state.token !== prev.token) {
      wsClient.disconnect();
      wsClient.connect();
    }
    if (!state.token && prev.token) {
      wsClient.disconnect();
    }
  });
}
